import { useEffect, useMemo, useState } from 'react';
import type { Incident } from '../data/demoData';
import { apiFetch, ApiError } from '../lib/apiClient';

interface IncidentExplainabilityRule {
  id: string;
  displayName?: string;
  kind?: string;
  query?: string;
  querySource?: string;
  queryFrequency?: string;
  queryPeriod?: string;
  severity?: string;
  resourceId?: string;
}

interface IncidentExplainabilityAlert {
  id: string;
  title?: string;
  productName?: string;
  providerName?: string;
  alertRuleId?: string;
  alertRuleResourceId?: string;
  alertRuleTemplateName?: string;
  createdTimeUtc?: string;
}

interface IncidentTriggeringRow {
  cells: Record<string, unknown>;
  summary: string;
}

interface IncidentExplainability {
  ok: boolean;
  incidentId: string;
  incidentNumber?: string;
  armIncidentName?: string;
  title?: string;
  fetchedAt: string;
  mode: string;
  analyticRules: IncidentExplainabilityRule[];
  alerts: IncidentExplainabilityAlert[];
  ruleLogic: string;
  querySource: string;
  generatedEvidenceQuery: string;
  matchingFields: string[];
  matchingEntities: string[];
  sourceTables: string[];
  groupingReasons: string[];
  recommendedTuning: string[];
  triggeringRows: {
    columns: string[];
    rows: IncidentTriggeringRow[];
    rowCount: number;
    sampled: number;
  };
  timeRange: { fromUtc: string; toUtc: string };
  diagnostics: string[];
}

interface Props {
  incident: Incident;
  customerId: string;
  onRunKql?: (query: string) => void;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function asText(value: unknown, max = 220): string {
  const text = String(value ?? '').replace(/[\x00-\x1F\x7F]/g, ' ').replace(/\s{2,}/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function modeLabel(mode: string): string {
  switch (mode) {
    case 'analyticRuleQuery': return 'Analytic rule KQL';
    case 'alertEmbeddedQuery': return 'Alert embedded KQL';
    case 'alertPivotFallback': return 'Alert/incident pivot fallback';
    case 'partial': return 'Partial fallback';
    default: return mode || 'Unknown';
  }
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1400);
        }).catch(() => setCopied(false));
      }}
      className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-muted"
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

function PillList({ values, empty }: { values: string[]; empty: string }) {
  if (!values.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <span key={value} className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-foreground">{value}</span>
      ))}
    </div>
  );
}

function CodeBlock({ code, title, onRunKql }: { code: string; title: string; onRunKql?: (query: string) => void }) {
  return (
    <div className="rounded-xl border bg-background overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
        <span className="text-xs font-semibold text-foreground">{title}</span>
        <div className="flex gap-2">
          {onRunKql && (
            <button type="button" onClick={() => onRunKql(code)} className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-muted">Open in Advanced Hunting</button>
          )}
          <CopyButton text={code} />
        </div>
      </div>
      <pre className="max-h-80 overflow-auto p-3 text-xs text-muted-foreground"><code>{code || '// No query text returned.'}</code></pre>
    </div>
  );
}

function TriggeringRowsTable({ rows, columns }: { rows: IncidentTriggeringRow[]; columns: string[] }) {
  const visibleColumns = useMemo(() => {
    const preferred = ['TimeGenerated', 'SourceTable', 'AlertName', 'DisplayName', 'Title', 'UserPrincipalName', 'IPAddress', 'Computer', 'DeviceName', 'Summary'];
    const selected = preferred.filter((c) => columns.some((x) => x.toLowerCase() === c.toLowerCase()));
    const remaining = columns.filter((c) => !selected.some((x) => x.toLowerCase() === c.toLowerCase())).slice(0, Math.max(0, 8 - selected.length));
    return [...selected, ...remaining].slice(0, 8);
  }, [columns]);

  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">No triggering rows were returned by the bounded evidence query. Check diagnostics and the generated KQL.</p>;
  }

  return (
    <div className="overflow-auto rounded-xl border">
      <table className="min-w-full divide-y divide-border text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            {visibleColumns.map((col) => <th key={col} className="px-3 py-2 text-left font-semibold">{col}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {rows.map((row, idx) => (
            <tr key={idx} className="align-top hover:bg-muted/30">
              {visibleColumns.map((col) => {
                const key = Object.keys(row.cells).find((k) => k.toLowerCase() === col.toLowerCase()) ?? col;
                return <td key={col} className="max-w-[280px] px-3 py-2 text-muted-foreground">{asText(row.cells[key], col === 'Summary' ? 420 : 180)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <details className="border-t bg-muted/20 p-3">
        <summary className="cursor-pointer text-xs font-medium text-foreground">Row summaries</summary>
        <div className="mt-2 space-y-2">
          {rows.map((row, idx) => <div key={idx} className="rounded-lg bg-background p-2 text-xs text-muted-foreground">{row.summary || 'No row summary.'}</div>)}
        </div>
      </details>
    </div>
  );
}

export function IncidentExplainabilityPanel({ incident, customerId, onRunKql }: Props) {
  const [data, setData] = useState<IncidentExplainability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const incidentId = String(incident.armIncidentName || incident.id || incident.incidentNumber || '').trim();
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (incident.armIncidentName) params.set('armIncidentName', incident.armIncidentName);
    if (incident.incidentNumber) params.set('incidentNumber', incident.incidentNumber);
    if (incident.title) params.set('title', incident.title);
    params.set('limit', '25');

    setLoading(true);
    setError(null);
    setData(null);
    apiFetch<IncidentExplainability>(`/incidents/${encodeURIComponent(incidentId)}/explainability?${params.toString()}`)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.forbidden) setError('Your role does not have permission to view incident explainability. Enable Incidents → View Incident Explainability in Permissions Master.');
        else setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, incident.armIncidentName, incident.id, incident.incidentNumber, incident.title]);

  if (loading) {
    return (
      <section className="mt-4 rounded-2xl border bg-card p-5 shadow-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-64 rounded bg-muted" />
          <div className="grid gap-3 md:grid-cols-3"><div className="h-20 rounded bg-muted" /><div className="h-20 rounded bg-muted" /><div className="h-20 rounded bg-muted" /></div>
          <div className="h-40 rounded bg-muted" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-700 dark:text-red-300">
        <strong>Incident explainability unavailable:</strong> {error}
      </section>
    );
  }

  if (!data) return null;

  const primaryRule = data.analyticRules[0];

  return (
    <section className="mt-4 space-y-4 rounded-2xl border bg-background/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Why Was This Incident Created?</h3>
          <p className="text-sm text-muted-foreground">
            This panel reconstructs the Sentinel incident trigger path by resolving linked alerts, analytic-rule KQL and a bounded sample of triggering rows.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Fetched: {formatDate(data.fetchedAt)} · Window: {formatDate(data.timeRange.fromUtc)} → {formatDate(data.timeRange.toUtc)}</p>
        </div>
        <span className="rounded-full border bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">{modeLabel(data.mode)}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Linked Alerts</div><div className="text-2xl font-semibold">{data.alerts.length}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Analytic Rules</div><div className="text-2xl font-semibold">{data.analyticRules.length}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Trigger Rows</div><div className="text-2xl font-semibold">{data.triggeringRows.rowCount}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Source Tables</div><div className="text-2xl font-semibold">{data.sourceTables.length}</div></div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-2 font-semibold text-foreground">Analytic Rule</h4>
          {primaryRule ? (
            <div className="space-y-1 text-sm">
              <div className="font-medium text-foreground">{primaryRule.displayName || primaryRule.id}</div>
              <div className="text-xs text-muted-foreground">Source: {primaryRule.querySource || data.querySource}</div>
              <div className="text-xs text-muted-foreground">Kind: {primaryRule.kind || '—'} · Severity: {primaryRule.severity || '—'} · Frequency: {primaryRule.queryFrequency || '—'} · Period: {primaryRule.queryPeriod || '—'}</div>
              {primaryRule.resourceId && <div className="break-all text-xs text-muted-foreground">Resource: {primaryRule.resourceId}</div>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No analytic rule metadata was resolved. The panel used the generated fallback query source: {data.querySource}</p>
          )}
        </div>

        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-2 font-semibold text-foreground">Grouping Reason</h4>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {data.groupingReasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ol>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-2 font-semibold text-foreground">Matching Fields</h4>
          <PillList values={data.matchingFields} empty="No strong matching fields were inferred from the returned query/rows." />
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-2 font-semibold text-foreground">Matching Entities</h4>
          <PillList values={data.matchingEntities} empty="No recurring entity values were inferred from the returned rows." />
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-2 font-semibold text-foreground">Source Tables</h4>
          <PillList values={data.sourceTables} empty="No source table could be inferred." />
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <h4 className="mb-3 font-semibold text-foreground">Linked Alerts</h4>
        {data.alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No linked alert details were returned by Sentinel.</p>
        ) : (
          <div className="space-y-2">
            {data.alerts.slice(0, 12).map((alert) => (
              <div key={`${alert.id}-${alert.title}`} className="rounded-xl border bg-muted/20 p-3 text-sm">
                <div className="font-medium text-foreground">{alert.title || alert.id}</div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>Created: {formatDate(alert.createdTimeUtc)}</span>
                  <span>Product: {alert.productName || '—'}</span>
                  <span>Provider: {alert.providerName || '—'}</span>
                  <span>Rule: {alert.alertRuleId || alert.alertRuleTemplateName || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="font-semibold text-foreground">Triggering Rows</h4>
            <p className="text-xs text-muted-foreground">Showing {data.triggeringRows.sampled} sampled row{data.triggeringRows.sampled === 1 ? '' : 's'} from the bounded evidence query.</p>
          </div>
          <CopyButton text={JSON.stringify(data.triggeringRows.rows.map((r) => r.cells), null, 2)} label="Copy Rows JSON" />
        </div>
        <TriggeringRowsTable rows={data.triggeringRows.rows} columns={data.triggeringRows.columns} />
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <h4 className="mb-3 font-semibold text-foreground">Recommended Tuning Options</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {data.recommendedTuning.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>

      <CodeBlock code={data.ruleLogic || data.generatedEvidenceQuery} title="Resolved Analytic Rule Logic" onRunKql={onRunKql} />

      <details className="rounded-xl border bg-card p-3 text-sm">
        <summary className="cursor-pointer font-medium text-foreground">Generated Bounded Evidence Query & Diagnostics</summary>
        <div className="mt-3 space-y-3">
          <CodeBlock code={data.generatedEvidenceQuery} title="Bounded Evidence Query" onRunKql={onRunKql} />
          {data.diagnostics.length > 0 ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
              <div className="mb-1 text-xs font-semibold text-amber-900 dark:text-amber-100">Diagnostics</div>
              <ul className="list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
                {data.diagnostics.map((diag) => <li key={diag}>{diag}</li>)}
              </ul>
            </div>
          ) : <p className="text-xs text-muted-foreground">No diagnostics were returned.</p>}
        </div>
      </details>
    </section>
  );
}

export default IncidentExplainabilityPanel;
